import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import remoteApi from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { useRealtime } from "../contexts/RealtimeContext";
import { useTranslation } from "react-i18next";
import {
  MessageCircle,
  Plus,
  Clock,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Loader2,
} from "lucide-react";

export default function TicketsList() {
  const { user } = useAuth();
  const { lastEvent } = useRealtime();
  const { t } = useTranslation();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);

  const getStatusColor = (ticketStatus) => {
    switch (ticketStatus?.toLowerCase()) {
      case "open":
        return {
          bg: "bg-blue-100 text-blue-800 border-blue-200",
          icon: <AlertCircle className="w-3 h-3" />,
          label: t("ticket.status.open"),
        };
      case "in_progress":
      case "in-progress":
        return {
          bg: "bg-yellow-100 text-yellow-800 border-yellow-200",
          icon: <Loader2 className="w-3 h-3" />,
          label: t("ticket.status.in_progress"),
        };
      case "resolved":
        return {
          bg: "bg-green-100 text-green-800 border-green-200",
          icon: <CheckCircle2 className="w-3 h-3" />,
          label: t("ticket.status.resolved"),
        };
      case "closed":
        return {
          bg: "bg-gray-100 text-gray-800 border-gray-200",
          icon: <XCircle className="w-3 h-3" />,
          label: t("ticket.status.closed"),
        };
      default:
        return {
          bg: "bg-gray-100 text-gray-800 border-gray-200",
          icon: <MessageCircle className="w-3 h-3" />,
          label: ticketStatus || t("ticket.unknown"),
        };
    }
  };

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const res = await remoteApi.listTickets();
        setTickets(res.data?.tickets || res.tickets || []);
      } catch (err) {
        console.error("Failed to load tickets", err?.message || err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [user]);

  useEffect(() => {
    // If realtime event comes in and is ticket_message or ticket_update, refresh list
    if (
      lastEvent &&
      lastEvent.eventType &&
      lastEvent.eventType.startsWith("ticket_")
    ) {
      (async () => {
        try {
          const res = await remoteApi.listTickets();
          setTickets(res.data?.tickets || res.tickets || []);
        } catch (err) {
          // ignore
        }
      })();
    }
  }, [lastEvent]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <MessageCircle className="w-8 h-8 text-primary-500" />
            <h2 className="text-2xl font-semibold text-gray-800">
              {t("ticket.my_tickets")}
            </h2>
          </div>
          <Link
            to="/new-ticket"
            className="bg-primary-500 hover:bg-primary-600 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition duration-200 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {t("ticket.create_ticket")}
          </Link>
        </div>

        {loading && (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
            <p className="text-gray-600">{t("ticket.loading")}</p>
          </div>
        )}

        {!loading && tickets.length === 0 && (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              {t("ticket.no_tickets")}
            </h3>
            <p className="text-gray-600 mb-6">{t("ticket.no_tickets_desc")}</p>
            <Link
              to="/new-ticket"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition duration-200"
            >
              <Plus className="w-4 h-4" />
              {t("ticket.create_first_ticket")}
            </Link>
          </div>
        )}

        <div className="grid gap-4">
          {tickets.map((ticket) => {
            const statusInfo = getStatusColor(ticket.status);
            const updatedDate = new Date(ticket.updatedAt).toLocaleDateString(
              "es-ES",
              {
                year: "numeric",
                month: "short",
                day: "numeric",
              }
            );
            const updatedTime = new Date(ticket.updatedAt).toLocaleTimeString(
              "es-ES",
              {
                hour: "2-digit",
                minute: "2-digit",
              }
            );

            return (
              <Link
                key={ticket.ticket_id}
                to={`/tickets/${ticket.ticket_id}`}
                className="block bg-white rounded-xl shadow-md hover:shadow-lg transition duration-200 border border-gray-100 hover:border-primary-200 group"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-800 group-hover:text-primary-700 transition-colors truncate">
                        {ticket.subject}
                      </h3>
                    </div>
                    <div
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ml-4 ${statusInfo.bg}`}
                    >
                      {statusInfo.icon}
                      <span>{statusInfo.label}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Clock className="w-4 h-4" />
                    <span>
                      {t("ticket.last_update")}: {updatedDate}{" "}
                      {t("ticket.at_time")} {updatedTime}
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
